import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarketType } from "../../../Utils";
import { createPredictionRequest } from "../../../redux/predictor";

/**
 * Custom hook for managing prediction submission
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} fansUnitedSDK - The FansUnited SDK instance
 * @param {Array} filteredMatches - Array of filtered matches
 * @param {Object} currentScores - Current scores object
 * @returns {Object} - Submit function and loading state
 */
export const useSubmitPredictions = (dispatch, fansUnitedSDK, filteredMatches, currentScores) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { t } = useTranslation();

	/**
	 * Submit all predictions for the current matches
	 * @returns {Promise<Object>} - Result object with success status, optional error, and predictions count
	 */
	const submitAllPredictions = useCallback(async () => {
		setIsSubmitting(true);

		try {
			// Submit predictions for all matches
			const promises = filteredMatches.map((match) => {
				const score = currentScores[match.id] || { homeTeamScore: 0, awayTeamScore: 0 };
				return dispatch(
					createPredictionRequest(
						fansUnitedSDK,
						match.id,
						MarketType.CORRECT_SCORE_ADVANCED,
						`${score.homeTeamScore}:${score.awayTeamScore}`,
					),
				);
			});

			await Promise.all(promises);

			return {
				success: true,
				predictionsCount: filteredMatches.length,
			};
		} catch (error) {
			return {
				success: false,
				error: error.data?.error?.message || t("Error saving predictions"),
				predictionsCount: 0,
			};
		} finally {
			setIsSubmitting(false);
		}
	}, [dispatch, fansUnitedSDK, filteredMatches, currentScores, t]);

	return {
		submitAllPredictions,
		isSubmitting,
	};
};
